import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Modal} from "@ignitesales/neuro-application/src/neuro-ui/lib/index";
import {PrintButton, ShareViaEmailForm, useNeuroMessages, triggerIgniteAction, useDispatch, ShareViaSmsForm, notifySuccess} from "@ignitesales/neuro-application";
import {useDialogueGuideSettings} from "../hooks/useDialogueGuideSettings.mjs";
import { ActionService } from '@ignitesales/neuro-application/src/neuro-guide/services/ActionService.mjs';
import { useEffect } from 'react';
import GuideApiService from '@ignitesales/neuro-application/src/neuro-guide/services/GuideApiService.mjs';
import ReactSVG from 'react-svg';
import { useActionTrigger } from '../hooks/useActionTrigger.mjs';

export const ShareActionTypes = {
    showAll: 'showAll', email: 'shareViaEmail', textMessage: 'shareViaSms', print: 'print'
};

const ShareActions = ({isActive, onClose, isModal = true, buttonOrder = ['email', 'textMessage', 'print'], iconConfig = {}, showContentInModal = false, shouldPrintOpenDetailsPage = false}) => {
    const { useTextResultsAction, useEmailResultsAction } = useDialogueGuideSettings();
    const {getMessage} = useNeuroMessages();
    const dispatch = useDispatch();
    const [actionType, setActionType] = useState(ShareActionTypes.showAll);
    const [emailActions, setEmailActions] = useState([]);
    const [smsActions, setSMSActions] = useState([]);
    const [hasActionTriggerEmail, setHasActionTriggerEmail] = useState(false);
    const [hasActionTriggerSMS, setHasActionTriggerSMS] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showSmsModal, setShowSmsModal] = useState(false);
    const componentClass = 'shareActions';
    
    // Use action trigger hook to handle pop/slide actions
    const { triggerActions } = useActionTrigger();

    useEffect(()=> {
        const fetchActions = async () => {
            const eActions = await ActionService.getActionsByTriggerType('shareEmail');
            const sActions = await ActionService.getActionsByTriggerType('shareSMS');
            setHasActionTriggerEmail(eActions && Array.isArray(eActions) && eActions.length);
            setHasActionTriggerSMS(sActions && Array.isArray(sActions) && sActions.length);
            setEmailActions(eActions);
            setSMSActions(sActions);
        }

        try {
            fetchActions();
        } catch (error) {
            console.error('error', error);
        }
    }, [])

    const actionClicked = actionName => {
        dispatch(triggerIgniteAction({actionName: `${actionName}Clicked`, value: 'started'}));
        
        if (showContentInModal) {
            if (actionName === ShareActionTypes.email) {
                setShowEmailModal(true);
            } else if (actionName === ShareActionTypes.textMessage) {
                setShowSmsModal(true);
            }
        } else {
            setActionType(actionName);
        }
    };

    const onCloseModal = () => {
        if (isModal && onClose) {
            onClose();
        }
        setTimeout(() => setActionType(ShareActionTypes.showAll), 500);
    }

    const onCloseEmailModal = () => {
        setShowEmailModal(false);
    };

    const onCloseSmsModal = () => {
        setShowSmsModal(false);
    };

    const triggerAllActions = async (actions, type = "shareEmail") => {
        // Use the hook's triggerActions which handles pop/slide UI
        await triggerActions(actions, type);
    }

    const actionCompleted = async (emailOrNumber) => {
        if(actionType == ShareActionTypes.email && hasActionTriggerEmail){
            if(!GuideApiService.lead) GuideApiService.lead = {};
            GuideApiService.lead.email = emailOrNumber;
            await triggerAllActions(emailActions, 'shareEmail');
        }
        if(actionType == ShareActionTypes.textMessage && hasActionTriggerSMS){
            if(!GuideApiService.lead) GuideApiService.lead = {};
            GuideApiService.lead.phone = emailOrNumber;
            await triggerAllActions(smsActions, 'shareSMS');
        }
        notifySuccess(`${actionType}.sent`);
        
        if (showContentInModal) {
            if (actionType === ShareActionTypes.email) {
                onCloseEmailModal();
            } else if (actionType === ShareActionTypes.textMessage) {
                onCloseSmsModal();
            }
        } else {
            onCloseModal();
        }
    };

    const handleEmailCompleted = async (emailOrNumber) => {
        if(hasActionTriggerEmail){
            if(!GuideApiService.lead) GuideApiService.lead = {};
            GuideApiService.lead.email = emailOrNumber;
            await triggerAllActions(emailActions, 'shareEmail');
        }
        notifySuccess('email.sent');
        onCloseEmailModal();
    };

    const handleSmsCompleted = async (phoneOrNumber) => {
        if(hasActionTriggerSMS){
            if(!GuideApiService.lead) GuideApiService.lead = {};
            GuideApiService.lead.phone = phoneOrNumber;
            await triggerAllActions(smsActions, 'shareSMS');
        }
        notifySuccess('textMessage.sent');
        onCloseSmsModal();
    };

    const renderButton = (buttonType) => {
        const iconInfo = iconConfig[buttonType];
        const renderIcon = (iconSrc) => {
            if (!iconSrc) return null;
            return <ReactSVG src={iconSrc} className="action-btn__icon" />;
        };

        const renderButtonContent = (text, iconSrc, position = 'left') => {
            if (!iconSrc) return text;
            
            if (position === 'left') {
                return (
                    <>
                        {renderIcon(iconSrc, position)}
                        {text}
                    </>
                );
            } else {
                return (
                    <>
                        {text}
                        {renderIcon(iconSrc, position)}
                    </>
                );
            }
        };

        switch(buttonType) {
            case 'email':
                return useEmailResultsAction !== false && 
                    <button key="email" className="btn btn-outline-secondary" onClick={() => actionClicked(ShareActionTypes.email)}>
                        {renderButtonContent('Email', iconInfo?.src, iconInfo?.position)}
                    </button>;
            case 'textMessage':
                return useTextResultsAction !== false && 
                    <button key="textMessage" className="btn btn-outline-secondary" onClick={() => actionClicked(ShareActionTypes.textMessage)}>
                        {renderButtonContent('Text Message', iconInfo?.src, iconInfo?.position)}
                    </button>;
            case 'print':
                return <PrintButton key="print" className="btn btn-outline-secondary" onClick={isModal ? onClose : undefined} shouldOpenDetailsPage={shouldPrintOpenDetailsPage}>
                    {renderButtonContent('Print', iconInfo?.src, iconInfo?.position)}
                </PrintButton>;
            default:
                return null;
        }
    };

    const allOptionsRender = <div className={`${componentClass}__buttons`}>
        {buttonOrder.map(buttonType => renderButton(buttonType))}
    </div>;

    const content = (
        <>
            {actionType === ShareActionTypes.showAll && allOptionsRender}
            {actionType === ShareActionTypes.email && <ShareViaEmailForm skipSending={hasActionTriggerEmail} onSubmit={actionCompleted}/>}
            {actionType === ShareActionTypes.textMessage && <ShareViaSmsForm skipSending={hasActionTriggerSMS} onSubmit={actionCompleted}/>}
        </>
    );

    if (!isModal) {
        return (
            <>
                <div className={componentClass}>
                    {content}
                    {showContentInModal && (
                    <>
                        <Modal show={showEmailModal} onClose={onCloseEmailModal} title="Send by Email">
                            <ShareViaEmailForm skipSending={hasActionTriggerEmail} onSubmit={handleEmailCompleted} />
                        </Modal>
                        <Modal show={showSmsModal} onClose={onCloseSmsModal} title="Send by Text Message">
                            <ShareViaSmsForm skipSending={hasActionTriggerSMS} onSubmit={handleSmsCompleted} />
                        </Modal>
                    </>
                )}
                </div>
            </>
        );
    }

    if (!isActive) {
        return null;
    }

    return (
        <>
            <Modal nodeId='root' className={componentClass} onClose={onCloseModal} show={isActive} title={getMessage('shareActions.title')}>
                {content}
            </Modal>
        </>
    );
};

ShareActions.propTypes = {
    isActive: PropTypes.bool, 
    onClose: PropTypes.func,
    isModal: PropTypes.bool,
    buttonOrder: PropTypes.arrayOf(PropTypes.oneOf(['email', 'textMessage', 'print'])),
    iconConfig: PropTypes.shape({
        email: PropTypes.shape({
            src: PropTypes.string,
            position: PropTypes.oneOf(['left', 'right'])
        }),
        textMessage: PropTypes.shape({
            src: PropTypes.string,
            position: PropTypes.oneOf(['left', 'right'])
        }),
        print: PropTypes.shape({
            src: PropTypes.string,
            position: PropTypes.oneOf(['left', 'right'])
        })
    }),
    showContentInModal: PropTypes.bool,
    shouldPrintOpenDetailsPage: PropTypes.bool
};

export default ShareActions;